import React, {useEffect, useRef, useState} from 'react';
import {CheckIcon, XMarkIcon} from '@heroicons/react/24/outline';

import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  FieldError,
  Input,
  Label,
} from '@design-system';

import {useOnboarding} from '../Onboarding/useOnboarding';
import {MIN_YEAR_OF_BIRTH_PARENT_TOOL, MAX_YEAR_OF_BIRTH_PARENT_TOOL} from '../Util/constants';
import useRetryFocus, {focusIsFree} from '../Util/useRetryFocus';

/**
 * The Professional current-stage finder.
 *
 * The questions and their order are the shared onboarding pages the MUI
 * version composed — date of birth, left school, then either the leaving date
 * or the school year and an expected leaving date — rebuilt here as native
 * controls so the finder renders no MUI. The shared pages stay as they are for
 * their Parent consumer.
 *
 * Every branch ends in one guarded calculation: resolve a leaving date, then
 * the stage dates, then the current stage. A second request while one is in
 * flight is ignored, a failure keeps every answer and offers Retry, and a
 * result that arrives after the finder was closed is dropped.
 */

const TITLE_ID = 'find-current-stage-title';
const DESCRIPTION_ID = 'find-current-stage-description';
const FORM_ID = 'find-current-stage-form';

const MONTH_ID = 'find-current-stage-month';
const YEAR_ID = 'find-current-stage-year';
const DOB_HELP_ID = 'find-current-stage-dob-help';
const LEFT_SCHOOL_HELP_ID = 'find-current-stage-left-school-help';
const SCHOOL_YEAR_HELP_ID = 'find-current-stage-school-year-help';
const HAS_DATE_HELP_ID = 'find-current-stage-has-date-help';
const LEAVING_DATE_ID = 'find-current-stage-leaving-date';
const LEAVING_DATE_HELP_ID = 'find-current-stage-leaving-date-help';

const errorId = (id) => `${id}-error`;
const describedBy = (...ids) => ids.filter(Boolean).join(' ') || undefined;

const LEGEND_CLASSES = 'mb-1 p-0 text-lg font-semibold text-(--compass-on-surface)';
const HELP_CLASSES = 'm-0 mb-3 text-sm text-(--compass-on-surface)';

// Geometry matches the Input primitive, raised to the 44px house target.
const SELECT_CLASSES = [
  'block h-11 w-full rounded-md border px-3 text-sm',
  'border-(--compass-border) bg-(--compass-surface) text-(--compass-on-surface)',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
  'aria-[invalid=true]:border-(--compass-border-invalid)',
].join(' ');

/** One numbered question. List position carries the number the marker shows. */
const Step = ({number, children}) => (
  <li className="flex min-w-0 gap-3 border-t border-(--compass-border) pt-4 first:border-t-0 first:pt-0">
    <span
      aria-hidden="true"
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--compass-primary) text-sm font-semibold text-(--compass-on-primary)">
      {number}
    </span>
    <div className="min-w-0 flex-1">{children}</div>
  </li>
);

/** A yes/no question answered by one of two toggle buttons. */
const YesNo = ({name, value, onChange, describedById, error, firstRef}) => (
  <div className="flex flex-wrap gap-3">
    {[
      ['yes', 'Yes', true],
      ['no', 'No', false],
    ].map(([suffix, label, answer]) => {
      const pressed = value === answer;

      return (
        <Button
          key={suffix}
          ref={suffix === 'yes' ? firstRef : undefined}
          type="button"
          name={`${name}-${suffix}`}
          variant={pressed ? 'default' : 'outline'}
          className="min-h-11 min-w-24"
          aria-pressed={pressed}
          aria-describedby={describedBy(describedById, error ? errorId(name) : null)}
          onClick={() => onChange(answer)}>
          {pressed ? <CheckIcon className="size-5" aria-hidden="true" /> : null}
          {label}
        </Button>
      );
    })}
  </div>
);

/**
 * Loads one list of answer choices while `enabled`, again whenever `key`
 * changes, and on `retry`. A response for a superseded key is dropped.
 * `retried` says whether the current load, or the one it settled, was asked for
 * by Retry; a Retry pressed while one is in flight is ignored.
 */
const useChoices = (load, key, enabled) => {
  const [state, setState] = useState({status: 'idle', data: undefined, retried: false});
  const [attempt, setAttempt] = useState(0);
  const lastAttempt = useRef(attempt);
  const retryPending = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let current = true;
    const retried = attempt !== lastAttempt.current;
    lastAttempt.current = attempt;
    setState({status: 'loading', data: undefined, retried});
    load().then(
      (data) => {
        retryPending.current = false;
        if (current) setState({status: 'ready', data, retried});
      },
      () => {
        retryPending.current = false;
        if (current) setState({status: 'failed', data: undefined, retried});
      }
    );

    return () => {
      current = false;
    };
  }, [enabled, key, attempt]);

  const retry = () => {
    if (retryPending.current) return;
    retryPending.current = true;
    setAttempt((count) => count + 1);
  };

  return {...state, retry};
};

/**
 * The announced loading and failure states of a choice list. A failure follows
 * the answer that asked for the list, which keeps focus; it takes focus itself
 * only when nothing else has it. It stays on screen, with the Retry that has
 * focus, while a retry is in flight and when it fails again; a retry that loads
 * the list hands focus to `focusRef`, its first answer, unless the person has
 * moved to another control meanwhile.
 */
const ChoicesState = ({choices, label, focusRef, children}) => {
  const retrying = choices.retried && choices.status === 'loading';
  const loaded = choices.status === 'ready';
  const {failureRef} = useRetryFocus(
    {failed: choices.status === 'failed' || retrying, focusFirstFailure: 'when-unfocused'},
    choices.retry
  );

  useEffect(() => {
    if (loaded && choices.retried && focusIsFree()) focusRef.current?.focus();
  }, [loaded]);

  if (choices.status === 'failed' || retrying) {
    return (
      <Alert
        ref={failureRef}
        variant="error"
        className="my-0"
        action={
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            aria-busy={retrying ? true : undefined}
            onClick={choices.retry}>
            Retry
          </Button>
        }>
        {`We couldn't load the ${label}. Please try again.`}
      </Alert>
    );
  }

  if (!loaded) {
    return (
      <p role="status" className="m-0 text-sm text-(--compass-on-surface)">
        {`Loading ${label}…`}
      </p>
    );
  }

  return children;
};

const FindCurrentStageDialog = ({open, handleClose, handleResult}) => {
  const {
    onboardingState,
    calculateSchoolYearFormChoices,
    calculateTransitionStageDates,
    calculateCurrentTransitionStage,
    calculatePotentialExpectedLeavingDates,
  } = useOnboarding();
  const [monthOfBirth, setMonthOfBirth] = useState('');
  const [yearOfBirth, setYearOfBirth] = useState('');
  const [hasLeftSchool, setHasLeftSchool] = useState();
  const [schoolYear, setSchoolYear] = useState();
  const [hasExpectedLeavingDate, setHasExpectedLeavingDate] = useState();
  const [expectedLeavingDate, setExpectedLeavingDate] = useState({term: '', year: ''});
  const [submitted, setSubmitted] = useState(false);
  const [calculation, setCalculation] = useState('idle');

  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const leftSchoolRef = useRef(null);
  const schoolYearRef = useRef(null);
  const hasDateRef = useRef(null);
  const leavingDateRef = useRef(null);
  const failureRef = useRef(null);

  // `pending` blocks a second calculation synchronously, before the busy state
  // has rendered; `attempt` identifies the calculation a result belongs to.
  const pending = useRef(false);
  const attempt = useRef(null);

  const monthInt = parseInt(monthOfBirth);
  const yearInt = parseInt(yearOfBirth);
  const monthError =
    monthOfBirth === ''
      ? submitted && 'Enter a month.'
      : (monthInt > 12 || monthInt < 1) && 'Month must be between 1 and 12';
  const yearError =
    yearOfBirth === ''
      ? submitted && 'Enter a year.'
      : yearOfBirth.length !== 4
      ? 'Year must be 4 digits'
      : (yearInt < MIN_YEAR_OF_BIRTH_PARENT_TOOL || yearInt > MAX_YEAR_OF_BIRTH_PARENT_TOOL) &&
        `Year must be between ${MIN_YEAR_OF_BIRTH_PARENT_TOOL} and ${MAX_YEAR_OF_BIRTH_PARENT_TOOL}`;
  const hasValidBirthDate = monthOfBirth !== '' && yearOfBirth !== '' && !monthError && !yearError;

  const asksSchoolYear = hasValidBirthDate && hasLeftSchool === false;
  const asksLeavingDate =
    hasValidBirthDate &&
    (hasLeftSchool === true ||
      (asksSchoolYear && schoolYear !== undefined && hasExpectedLeavingDate === true));

  const schoolYears = useChoices(
    () => calculateSchoolYearFormChoices(monthOfBirth, yearOfBirth),
    `${monthOfBirth}/${yearOfBirth}`,
    asksSchoolYear
  );
  const leavingDates = useChoices(
    () =>
      calculatePotentialExpectedLeavingDates(
        monthOfBirth,
        yearOfBirth,
        schoolYear,
        !hasLeftSchool, // If the young person hasn't left school yet, hide past dates
        !!hasLeftSchool // If they have left school already, hide future dates
      ),
    `${monthOfBirth}/${yearOfBirth}/${schoolYear}/${hasLeftSchool}`,
    asksLeavingDate
  );

  // A selection counts only while the current list still offers it: a new
  // birth date or school year reloads the list and can leave it stale.
  const hasLeavingDate =
    leavingDates.status === 'ready' &&
    Boolean(
      leavingDates.data?.some(
        (item) =>
          item.term === expectedLeavingDate.term &&
          Number(item.year) === Number(expectedLeavingDate.year)
      )
    );
  const errors = {
    leftSchool:
      submitted && hasValidBirthDate && hasLeftSchool === undefined && 'Select yes or no.',
    schoolYear: submitted && asksSchoolYear && schoolYear === undefined && 'Select a school year.',
    hasDate:
      submitted &&
      asksSchoolYear &&
      schoolYear !== undefined &&
      hasExpectedLeavingDate === undefined &&
      'Select yes or no.',
    leavingDate:
      submitted &&
      asksLeavingDate &&
      !hasLeavingDate &&
      (hasLeftSchool ? 'Select the leaving date.' : 'Select an expected leaving date.'),
  };

  // `retrying` keeps the failure alert, and the Retry that has focus, mounted
  // until the new attempt settles.
  const calculate = async (resolveLeavingDate, {isRetry = false} = {}) => {
    if (pending.current) {
      return;
    }

    const current = {};
    pending.current = true;
    attempt.current = current;
    setCalculation(isRetry ? 'retrying' : 'pending');

    try {
      const leavingDate = await resolveLeavingDate();
      const stageDates = await calculateTransitionStageDates(leavingDate.term, leavingDate.year);
      const currentStage = await calculateCurrentTransitionStage(
        leavingDate.term,
        leavingDate.year
      );

      if (attempt.current === current) {
        setCalculation('idle');
        handleResult(stageDates, currentStage);
      }
    } catch {
      if (attempt.current === current) {
        setCalculation('failed');
      }
    } finally {
      // A closed visit already released the guard, and may have started another.
      if (attempt.current === current) {
        pending.current = false;
      }
    }
  };

  // The branch without a known date uses the earliest date Compass estimates.
  const calculateFromEarliestDate = (options) =>
    calculate(async () => {
      const possibleLeavingDates = await calculatePotentialExpectedLeavingDates(
        monthOfBirth,
        yearOfBirth,
        schoolYear,
        !hasLeftSchool // If the young person hasn't left school yet, hide past dates
      );

      return possibleLeavingDates[0];
    }, options);

  // Submit and Retry both validate and choose a branch from the answers on screen.
  const submitAnswers = (options) => {
    setSubmitted(true);

    const invalid = [
      [monthOfBirth === '' || monthError, monthRef],
      [yearOfBirth === '' || yearError, yearRef],
      [hasValidBirthDate && hasLeftSchool === undefined, leftSchoolRef],
      [asksSchoolYear && schoolYear === undefined, schoolYearRef],
      [
        asksSchoolYear && schoolYear !== undefined && hasExpectedLeavingDate === undefined,
        hasDateRef,
      ],
      [asksLeavingDate && !hasLeavingDate, leavingDateRef],
    ].find(([isInvalid]) => isInvalid);

    if (invalid) {
      invalid[1].current?.focus();
    } else if (asksSchoolYear && hasExpectedLeavingDate === false) {
      calculateFromEarliestDate(options);
    } else {
      const leavingDate = expectedLeavingDate;
      calculate(async () => leavingDate, options);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitAnswers();
  };

  const close = () => {
    // A calculation still in flight belongs to this visit; its result is
    // dropped and the next visit may calculate straight away.
    attempt.current = null;
    pending.current = false;
    setCalculation('idle');
    handleClose();
  };

  const isCalculating = calculation === 'pending' || calculation === 'retrying';

  // The alert follows the last question, so bring it into the scrolling body's
  // view. A failure that follows a submit takes focus, so it is read out with
  // Retry the next stop; Retry keeps focus through a retry that fails again.
  const previousCalculation = useRef(calculation);
  useEffect(() => {
    if (calculation === 'failed') {
      failureRef.current?.scrollIntoView?.({block: 'nearest'});
      if (previousCalculation.current === 'pending') {
        failureRef.current?.focus({preventScroll: true});
      }
    }
    previousCalculation.current = calculation;
  }, [calculation]);

  const onDigits = (setter) => (event) => {
    const value = event.target.value;
    // Allow clearing or only numeric input
    if (value === '' || /^[0-9]+$/.test(value)) {
      setter(value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      initialFocusRef={monthRef}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      className="max-w-2xl"
      data-testid="find-current-stage-dialog">
      <DialogHeader>
        <h2 id={TITLE_ID} className="m-0 flex-1 text-xl font-semibold">
          Find current stage
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11"
          aria-label="Close"
          data-testid="find-current-stage-close"
          onClick={close}>
          <XMarkIcon className="size-5" aria-hidden="true" />
        </Button>
      </DialogHeader>
      <DialogBody>
        <p id={DESCRIPTION_ID} className="m-0 mb-4 text-(--compass-on-surface)">
          Answer a few questions about the young person to find their stage in the transitions path.
        </p>
        <form id={FORM_ID} noValidate onSubmit={handleSubmit}>
          <ol className="m-0 flex list-none flex-col gap-4 p-0">
            <Step number={1}>
              <fieldset className="m-0 min-w-0 border-0 p-0">
                <legend className={LEGEND_CLASSES}>When was your young person born?</legend>
                <p id={DOB_HELP_ID} className={HELP_CLASSES}>
                  We use this to calculate their stage in the transitions path. We just need the
                  month and year.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="w-28">
                    <Label htmlFor={MONTH_ID} className="mb-1 block">
                      Month
                    </Label>
                    <Input
                      ref={monthRef}
                      id={MONTH_ID}
                      name="month"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={2}
                      placeholder="mm"
                      autoComplete="off"
                      className="h-11"
                      value={monthOfBirth}
                      onChange={onDigits(setMonthOfBirth)}
                      aria-invalid={monthError ? true : undefined}
                      aria-describedby={describedBy(DOB_HELP_ID, monthError && errorId(MONTH_ID))}
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor={YEAR_ID} className="mb-1 block">
                      Year
                    </Label>
                    <Input
                      ref={yearRef}
                      id={YEAR_ID}
                      name="year"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      placeholder="yyyy"
                      autoComplete="off"
                      className="h-11"
                      value={yearOfBirth}
                      onChange={onDigits(setYearOfBirth)}
                      aria-invalid={yearError ? true : undefined}
                      aria-describedby={describedBy(DOB_HELP_ID, yearError && errorId(YEAR_ID))}
                    />
                  </div>
                </div>
                <FieldError id={errorId(MONTH_ID)}>{monthError}</FieldError>
                <FieldError id={errorId(YEAR_ID)}>{yearError}</FieldError>
              </fieldset>
            </Step>

            {hasValidBirthDate ? (
              <Step number={2}>
                <fieldset className="m-0 min-w-0 border-0 p-0">
                  <legend className={LEGEND_CLASSES}>Has your young person left school?</legend>
                  <p id={LEFT_SCHOOL_HELP_ID} className={HELP_CLASSES}>
                    This works out their stage in the transitions path.
                  </p>
                  <YesNo
                    name="left-school"
                    value={hasLeftSchool}
                    onChange={setHasLeftSchool}
                    describedById={LEFT_SCHOOL_HELP_ID}
                    error={errors.leftSchool}
                    firstRef={leftSchoolRef}
                  />
                  <FieldError id={errorId('left-school')}>{errors.leftSchool}</FieldError>
                </fieldset>
              </Step>
            ) : null}

            {asksSchoolYear ? (
              <Step number={3}>
                <fieldset className="m-0 min-w-0 border-0 p-0">
                  <legend className={LEGEND_CLASSES}>
                    What school year is your young person in?
                  </legend>
                  <p id={SCHOOL_YEAR_HELP_ID} className={HELP_CLASSES}>
                    This works out their stage in the transitions path.
                  </p>
                  <ChoicesState choices={schoolYears} label="school years" focusRef={schoolYearRef}>
                    <div className="flex flex-col">
                      {schoolYears.data?.map((item, index) => {
                        const value = onboardingState?.schoolYears
                          ? onboardingState.schoolYears.indexOf(item)
                          : index;
                        const id = `find-current-stage-school-year-${index}`;

                        return (
                          <div key={item} className="flex items-center gap-3">
                            <input
                              ref={index === 0 ? schoolYearRef : undefined}
                              type="radio"
                              id={id}
                              name="school-year"
                              value={value}
                              checked={schoolYear === value}
                              onChange={() => setSchoolYear(value)}
                              aria-invalid={errors.schoolYear ? true : undefined}
                              aria-describedby={describedBy(
                                SCHOOL_YEAR_HELP_ID,
                                errors.schoolYear && errorId('school-year')
                              )}
                              className="size-5 shrink-0 accent-(--compass-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)"
                            />
                            <label
                              htmlFor={id}
                              className="flex min-h-11 flex-1 cursor-pointer items-center text-(--compass-on-surface)">
                              {item}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </ChoicesState>
                  <FieldError id={errorId('school-year')}>{errors.schoolYear}</FieldError>
                </fieldset>
              </Step>
            ) : null}

            {asksSchoolYear && schoolYear !== undefined ? (
              <Step number={4}>
                <fieldset className="m-0 min-w-0 border-0 p-0">
                  <legend className={LEGEND_CLASSES}>
                    Have you thought about an expected leaving date?
                  </legend>
                  <div id={HAS_DATE_HELP_ID} className={HELP_CLASSES}>
                    <p className="m-0">This works out your stage in the transitions path.</p>
                    <p className="m-0 mt-1">
                      If you don&apos;t have a date Compass estimates one based on your young
                      person&apos;s date of birth and current school year to give you the best
                      information.
                    </p>
                  </div>
                  <YesNo
                    name="has-leaving-date"
                    value={hasExpectedLeavingDate}
                    onChange={(answer) => {
                      setHasExpectedLeavingDate(answer);
                      if (!answer) {
                        calculateFromEarliestDate();
                      }
                    }}
                    describedById={HAS_DATE_HELP_ID}
                    error={errors.hasDate}
                    firstRef={hasDateRef}
                  />
                  <FieldError id={errorId('has-leaving-date')}>{errors.hasDate}</FieldError>
                </fieldset>
              </Step>
            ) : null}

            {asksLeavingDate ? (
              <Step number={hasLeftSchool ? 3 : 5}>
                <Label htmlFor={LEAVING_DATE_ID} className={`block ${LEGEND_CLASSES}`}>
                  {hasLeftSchool
                    ? 'When did your young person leave school?'
                    : "What is your young person's expected leaving date?"}
                </Label>
                <p id={LEAVING_DATE_HELP_ID} className={HELP_CLASSES}>
                  You can change it in your profile settings.
                </p>
                <ChoicesState
                  choices={leavingDates}
                  label="leaving dates"
                  focusRef={leavingDateRef}>
                  <select
                    ref={leavingDateRef}
                    id={LEAVING_DATE_ID}
                    name="expected-leaving-date"
                    className={SELECT_CLASSES}
                    value={
                      hasLeavingDate
                        ? `${expectedLeavingDate.term} ${expectedLeavingDate.year}`
                        : ''
                    }
                    onChange={(event) => {
                      const [term, year] = event.target.value.split(' ');
                      setExpectedLeavingDate(
                        year ? {term, year: parseInt(year)} : {term: '', year: ''}
                      );
                    }}
                    aria-invalid={errors.leavingDate ? true : undefined}
                    aria-describedby={describedBy(
                      LEAVING_DATE_HELP_ID,
                      errors.leavingDate && errorId(LEAVING_DATE_ID)
                    )}>
                    <option value="">
                      Please select{' '}
                      {hasLeftSchool ? 'the leaving date' : 'an expected leaving date'}
                    </option>
                    {leavingDates.data?.map((item) => (
                      <option key={`${item.term} ${item.year}`} value={`${item.term} ${item.year}`}>
                        {`${item.term}, ${item.year}`}
                      </option>
                    ))}
                  </select>
                </ChoicesState>
                <FieldError id={errorId(LEAVING_DATE_ID)}>{errors.leavingDate}</FieldError>
              </Step>
            ) : null}
          </ol>
        </form>
        {/* Mounted throughout so the busy text is announced when it appears. */}
        <p
          role="status"
          data-testid="find-current-stage-progress"
          className="m-0 text-sm text-(--compass-on-surface)">
          {isCalculating ? <span className="mt-4 block">Finding current stage…</span> : null}
        </p>
        {calculation === 'failed' || calculation === 'retrying' ? (
          <Alert
            ref={failureRef}
            variant="error"
            data-testid="find-current-stage-error"
            className="mb-0"
            action={
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                data-testid="find-current-stage-retry"
                aria-busy={calculation === 'retrying' ? true : undefined}
                onClick={() => submitAnswers({isRetry: true})}>
                Retry
              </Button>
            }>
            We couldn&apos;t work out the current stage. Your answers are kept. Please try again.
          </Alert>
        ) : null}
      </DialogBody>
      {/* One action, so the footer stays a single row at 400% zoom; the
          labelled Close above and Escape are the ways out. */}
      <DialogFooter>
        <Button
          type="submit"
          form={FORM_ID}
          className="min-h-11"
          data-testid="find-current-stage-submit"
          aria-busy={isCalculating ? true : undefined}>
          Find current stage
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default FindCurrentStageDialog;
